/**
 * RouteErrorBoundary — catches any unhandled render error in a route's tree
 * and shows a recovery UI instead of the iOS Safari blank screen.
 *
 * Critically:
 *   1) Records the crash to IndexedDB so we can extract it later (forensics).
 *   2) Provides "Reload", "Go to dashboard", and "Open Sync Queue" recovery paths.
 *   3) Surfaces the actual error message to the user so they can screenshot it
 *      and send it to support (no more "invisible error message").
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { AlertOctagon, RefreshCw, Home, Wrench, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { put, STORES } from '@/lib/offlineDB';

export class RouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    // Best-effort: persist crash log to IndexedDB so support can recover it.
    try {
      const crash = {
        id: `crash_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
        route: window.location.pathname,
        message: error?.message || String(error),
        stack: (error?.stack || '').slice(0, 4000),
        componentStack: (info?.componentStack || '').slice(0, 4000),
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      };
      // Use the offline_queue store with a special action type so the Sync Queue
      // Recovery page shows it alongside other items — and the user can Export
      // diagnostic JSON which will include this crash.
      put(STORES.OFFLINE_QUEUE, {
        id: crash.id,
        action_type: 'CLIENT_CRASH_LOG',
        payload: crash,
        org_id: null,
        created_at: crash.timestamp,
        status: 'failed',
        retries: 0,
        last_error: crash.message,
        last_error_class: 'unknown',
      }).catch(() => { /* ignore IndexedDB failures here — don't double-fault */ });
      // Also log to console for live debugging
      // eslint-disable-next-line no-console
      console.error('[RouteErrorBoundary]', crash);
    } catch { /* swallow; we're already in a fatal state */ }
  }

  copyError = async () => {
    const text = [
      `Route: ${window.location.pathname}`,
      `Error: ${this.state.error?.message || this.state.error}`,
      `Stack:`,
      this.state.error?.stack || '',
      `Component Stack:`,
      this.state.info?.componentStack || '',
      `User-Agent: ${navigator.userAgent}`,
      `When: ${new Date().toISOString()}`,
    ].join('\n');
    try {
      await navigator.clipboard.writeText(text);
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    } catch { /* clipboard may be blocked */ }
  };

  reset = () => this.setState({ hasError: false, error: null, info: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-10" data-testid="route-error-boundary">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center flex-shrink-0">
              <AlertOctagon className="w-5 h-5 text-red-600" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-lg text-gray-900">Something broke on this page</h1>
              <p className="text-sm text-gray-500 mt-1">
                Your work isn't lost — local drafts are still saved on this device.
              </p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-3 mb-4 max-h-40 overflow-y-auto">
            <p className="text-xs font-mono text-gray-700 break-words">
              {this.state.error?.message || String(this.state.error)}
            </p>
          </div>

          <div className="grid gap-2">
            <Button
              onClick={() => window.location.reload()}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="error-reload-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Reload page
            </Button>
            {this.props.developerMode && (
              <Link to="/sync-queue">
                <Button variant="outline" className="w-full" data-testid="error-open-sync-queue">
                  <Wrench className="w-4 h-4 mr-2" />
                  Open Sync Queue & recover data
                </Button>
              </Link>
            )}
            <Link to="/dashboard">
              <Button variant="outline" className="w-full" data-testid="error-home-btn">
                <Home className="w-4 h-4 mr-2" />
                Back to dashboard
              </Button>
            </Link>
            <Button
              variant="ghost"
              onClick={this.copyError}
              className="w-full text-gray-600"
              data-testid="error-copy-btn"
            >
              <Copy className="w-4 h-4 mr-2" />
              {this.state.copied ? 'Copied!' : 'Copy error details (for support)'}
            </Button>
          </div>

          <p className="text-[11px] text-gray-400 text-center mt-4">
            {this.props.developerMode
              ? 'This crash was logged on this device. Sync Queue → Export diagnostic to send it.'
              : 'Your work is safe locally. Contact support if this keeps happening.'}
          </p>
        </div>
      </div>
    );
  }
}

export default RouteErrorBoundary;
